<template>
  <div class="gallery app-page-wrapper absolute" data-cy="gallery">
    <div class="w-full h-full relative flex flex-col items-center">
      <menu-component menu-type="collection" :title="$t('gallery.collection')" />

      <section
        v-if="collection && collectionId"
        class="w-full h-full overflow-hidden safe-area bg-cover flex"
        :style="{
          backgroundImage: 'url(' + pathImages + 'gallery/BG_FULLSCREEN.avif)',
        }"
      >
        <div
          class="main-wrapper h-full flex justify-center items-start relative main-gallery-content-wrapper"
        >
          <gallery-sub-header
            show-picture-counter
            :progress-status="collection.stats.completed_percent"
            :locks-count="collection.stats.pictures_locked"
            :pictures-count="{
              collectedCount: Number(collection.stats.pictures_completed),
              totalCount: Number(collection.parameters.pictures_in_collection),
            }"
          >
            <span class="uppercase"> {{ replacedItemName }}</span>
          </gallery-sub-header>

          <app-scrollbar width="100%" height="100%" scroll="y" slice="y" class="flex mb-5 ml-14">
            <div class="w-full flex flex-wrap gap-4 mt-5 content-start">
              <gallery-collection-item
                v-for="picture in collection.pictures"
                :key="picture.id"
                :picture="picture"
                :personal-best="parseFloat(collection.personal_best)"
                is-picture
              />
            </div>
          </app-scrollbar>
        </div>

        <gallery-item-complete
          :is-completed="isCompleted"
          :is-claimable="canClaim"
          :rewards="collection.rewards"
          :img-id="collection.parameters?.id"
          @claim-reward="claimCollectionReward(collectionId)"
        />
      </section>

      <component-loading :is-loading="!collection" />
    </div>
  </div>
</template>

<script lang="ts">
import { pathImages } from '@/globalVariables'
import { defineComponent } from 'vue'
import GalleryCollectionItem from '../../components/Gallery/GalleryCollectionItem.vue'
import AppScrollbar from '../../components/GlobalComponents/AppScrollbar.vue'
import GalleryItemComplete from '@/components/Gallery/GalleryItemComplete.vue'
import GallerySubHeader from '@/components/Gallery/GallerySubHeader.vue'
import MenuComponent from '@/components/GlobalComponents/MenuComponent.vue'
import { mapState, mapActions } from 'pinia'
import { useGalleryCollectionsStore } from '@/store/pinia/gallery/collectionsStore'
import { GalleryCollectionState } from '@/interfaces/gallery/enums'

interface ComponentData {
  collectionId: string
  pathImages: typeof pathImages
  GalleryCollectionState: typeof GalleryCollectionState
}

export default defineComponent({
  name: 'GalleryCollection',

  components: {
    GalleryCollectionItem,
    AppScrollbar,
    GalleryItemComplete,
    GallerySubHeader,
    MenuComponent,
  },

  data(): ComponentData {
    return {
      collectionId: '',
      pathImages,
      GalleryCollectionState
    }
  },

  computed: {
    ...mapState(useGalleryCollectionsStore, ['collection']),

    replacedItemName(): string {
      const collectionName = this.collection?.parameters.name
      if (collectionName === 'collection100Meters') {
        return this.$replacePlaceholder(this.$t(`gallery.${collectionName}`), '%s', '100')
      }

      return this.$t(`gallery.${collectionName}`)
    },

    isCompleted(): boolean {
      return this.collection.state === GalleryCollectionState.CompletedClaimed
    },

    canClaim(): boolean {
      return this.collection.state === GalleryCollectionState.CompletedUnclaimed
    }
  },

  created(): void {
    this.collectionId = this.$route.params.id.toString() || ''

    if (!this.collectionId) {
      this.$router.push({ name: this.$getWebView('GalleryView') })
      return
    }

    this.loadCollection(this.collectionId)
  },

  methods: {
    ...mapActions(useGalleryCollectionsStore, [
      'loadCollection',
      'claimCollectionReward'
    ])
  }
})
</script>

<style lang="scss" scoped>
@import '@/assets/styles/views/gallery/gallery.scss';

.main-gallery-content-wrapper {
  padding-top: 5.5rem;
  padding-bottom: 1.375rem;
}
</style>
